import { useLocation, Navigate, Link } from "react-router-dom";
import { Phone, FileText, Check, MapPin, ArrowRight, HelpCircle } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CtaBand from "@/components/site/CtaBand";
import SEO from "@/components/site/SEO";
import { COMPANY } from "@/lib/siteData";
import { CITY_PAGES, SERVICE_PAGES } from "@/lib/localPages";

function BreadcrumbSchema({ label }) {
  const { pathname } = useLocation();
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://rmplomberiechauffagevmc.com/" },
      { "@type": "ListItem", position: 2, name: label, item: `https://rmplomberiechauffagevmc.com${pathname}` },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

function FaqSchema({ faqs }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

function renderPage(data, label) {
  return (
    <div data-testid={`local-page-${data.slug}`}>
      <SEO title={data.metaTitle} description={data.metaDescription} />
      <BreadcrumbSchema label={label} />
      <FaqSchema faqs={data.faqs} />

      <PageHero
        breadcrumb={label}
        kicker={data.kicker}
        highlight=""
        title={data.h1}
        subtitle={data.intro}
        useLogo
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {data.sections.map((s, i) => (
            <article key={s.title} className="mb-12 last:mb-0">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#001B35]">
                <span className="text-[#005BBB]">{String(i + 1).padStart(2, "0")}.</span> {s.title}
              </h2>
              <div className="mt-3 w-14 h-1 bg-[#005BBB] rounded-full" />
              <p className="mt-5 text-gray-700 leading-relaxed">{s.text}</p>
            </article>
          ))}

          <div className="mt-14 bg-[#F5F7FA] rounded-2xl p-8 grid sm:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-2">Besoin d'un devis ?</p>
              <h3 className="font-display text-xl font-bold text-[#001B35]">Contactez-nous, la réponse est rapide.</h3>
              <p className="text-sm text-gray-600 mt-2">Déplacement gratuit dans notre zone d'intervention.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${COMPANY.phoneTel}`} className="inline-flex items-center justify-center gap-2 bg-[#005BBB] hover:bg-[#001B35] text-white rounded-full px-5 py-3 font-semibold transition">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#D90000] hover:bg-[#b00000] text-white rounded-full px-5 py-3 font-semibold transition">
                <FileText className="w-4 h-4" /> Devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Questions fréquentes</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#001B35]">FAQ — {label}</h2>
          <div className="mt-4 w-14 h-1 bg-[#005BBB] rounded-full" />

          <div className="mt-8 space-y-4">
            {data.faqs.map((f) => (
              <details key={f.q} className="bg-white rounded-2xl border border-gray-100 p-5 group open:shadow-md transition">
                <summary className="flex items-start justify-between cursor-pointer list-none">
                  <span className="font-display font-bold text-[#001B35] pr-6">{f.q}</span>
                  <HelpCircle className="w-5 h-5 text-[#005BBB] flex-shrink-0 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking to other city / service pages */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Autres zones & services</p>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#001B35]">Nous intervenons également pour</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {Object.values(CITY_PAGES).filter(c => c.slug !== data.slug).map(c => (
              <Link key={c.slug} to={`/${c.slug}`} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5F7FA] hover:bg-[#001B35] hover:text-white border border-gray-200 rounded-full text-sm text-[#001B35] transition">
                <MapPin className="w-3.5 h-3.5" /> Plombier à {c.city}
              </Link>
            ))}
            {Object.values(SERVICE_PAGES).filter(s => s.slug !== data.slug).slice(0, 6).map(s => (
              <Link key={s.slug} to={`/${s.slug}`} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5F7FA] hover:bg-[#005BBB] hover:text-white border border-gray-200 rounded-full text-sm text-[#001B35] transition">
                <Check className="w-3.5 h-3.5" /> {s.kicker} {s.city}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}

export function CityPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const key = slug.replace(/^plombier-/, "");
  const data = key && CITY_PAGES[key];
  if (!data) return <Navigate to="/" replace />;
  return renderPage(data, `Plombier ${data.city}`);
}

export function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const data = slug && SERVICE_PAGES[slug];
  if (!data) return <Navigate to="/" replace />;
  return renderPage(data, `${data.kicker} ${data.city}`);
}
